// InstallGuide.jsx — 「ホーム画面に追加」の案内(帯)とやり方のシート
// まいウォレットの InstallGuide.jsx と同じ中身(全お客様アプリ共通の「ホーム画面に追加の案内」)。AKUTOブランド基準(白地・黒の主ボタン1つ・線アイコン)。
// - 帯: ホーム画面から開いていない時だけ出す。×で閉じたら同じ端末では出さない(下の小さなリンクからはいつでも開ける)
// - シート: 端末とブラウザで手順を出し分ける(iPhoneのSafari/Chrome・Android・LINEの中)
// - Android で「インストール」の合図(beforeinstallprompt)が来ていれば、ボタン1つで追加できる
import { useEffect, useState } from 'react';
import { EllipsisVertical, ExternalLink, Plus, Share, SquarePlus, X } from 'lucide-react';

const DISMISS_KEY = 'akuto.installHint.dismissed';

const readDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const writeDismissed = () => {
  try {
    window.localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* 保存できなくても案内が出るだけ */
  }
};

export const isStandalone = () =>
  (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
  (typeof navigator !== 'undefined' && navigator.standalone === true);

const detectPlatform = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /android/i.test(ua);
  const inLine = /\bLine\//i.test(ua);
  const iosChrome = isIOS && /CriOS/i.test(ua);
  return { isIOS, isAndroid, inLine, iosChrome, isMobile: isIOS || isAndroid };
};

// Android Chrome の「インストール」の合図を覚えておく(ページを開いた直後に来るので早めに拾う)
let deferredPrompt = null;
const promptListeners = new Set();
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event;
    promptListeners.forEach((fn) => fn(event));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    promptListeners.forEach((fn) => fn(null));
  });
}

export function useInstallState() {
  const [platform] = useState(detectPlatform);
  const [standalone] = useState(isStandalone);
  const [promptEvent, setPromptEvent] = useState(deferredPrompt);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    promptListeners.add(setPromptEvent);
    return () => promptListeners.delete(setPromptEvent);
  }, []);

  const canShow = !standalone && (platform.isMobile || Boolean(promptEvent));

  return {
    platform,
    promptEvent,
    canShow,
    showHint: canShow && !dismissed,
    dismiss: () => {
      writeDismissed();
      setDismissed(true);
    },
  };
}

// 案内の帯(残高カードの下に置く)
export function InstallHint({ onOpen, onDismiss, className = '' }) {
  return (
    <div className={`flex ${className} items-center gap-3 rounded-3xl border border-gray-100 bg-white py-3 pl-4 pr-2 shadow-sm`}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
        <SquarePlus size={20} strokeWidth={1.5} />
      </div>
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <p className="text-sm font-black text-gray-900">ホーム画面に追加</p>
        <p className="mt-0.5 text-xs font-bold leading-5 text-gray-500">次からはアイコンからすぐ開けます。</p>
      </button>
      <button
        type="button"
        onClick={onOpen}
        className="flex h-9 shrink-0 items-center rounded-full bg-gray-100 px-4 text-sm font-black text-gray-700 transition hover:bg-gray-200"
      >
        やり方
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="案内を閉じる"
        className="flex h-11 w-9 shrink-0 items-center justify-center text-gray-400 transition hover:text-gray-700"
      >
        <X size={18} strokeWidth={1.75} />
      </button>
    </div>
  );
}

function Step({ number, icon: Icon, title, note }) {
  return (
    <li className="flex items-start gap-3 py-4">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-black tabular-nums text-white">
        {number}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-1.5 text-sm font-black leading-6 text-gray-900">
          {title}
          {Icon ? (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700">
              <Icon size={16} strokeWidth={1.75} />
            </span>
          ) : null}
        </p>
        {note ? <p className="mt-0.5 text-xs font-bold leading-5 text-gray-500">{note}</p> : null}
      </div>
    </li>
  );
}

const stepsFor = (platform) => {
  if (platform.inLine) {
    return {
      lead: 'LINEの中では追加できません。いったんブラウザで開いてから追加します。',
      steps: [
        { icon: EllipsisVertical, title: '右上のメニューを押す' },
        { icon: ExternalLink, title: '「ブラウザで開く」を選ぶ', note: 'SafariやChromeで同じ画面が開きます。' },
        { title: '開いた画面で、もう一度この案内を開く' },
      ],
    };
  }
  if (platform.isIOS) {
    return {
      lead: null,
      steps: [
        {
          icon: Share,
          title: platform.iosChrome ? 'アドレスバーの右の共有ボタンを押す' : '画面下の共有ボタンを押す',
          note: platform.iosChrome ? null : '下にバーが無いときは、画面を少し上にずらすと出ます。',
        },
        { icon: SquarePlus, title: '「ホーム画面に追加」を選ぶ', note: '見つからないときは、一覧を下にずらしてください。' },
        { title: '右上の「追加」を押す' },
      ],
    };
  }
  return {
    lead: null,
    steps: [
      { icon: EllipsisVertical, title: '右上のメニューを押す' },
      { icon: SquarePlus, title: '「ホーム画面に追加」を選ぶ', note: '「アプリをインストール」と出る機種もあります。' },
      { title: '「追加」または「インストール」を押す' },
    ],
  };
};

// やり方のシート
export function InstallGuideSheet({ open, onClose, platform, promptEvent }) {
  const [installing, setInstalling] = useState(false);
  if (!open) return null;
  const { lead, steps } = stepsFor(platform);

  const installNow = async () => {
    if (!promptEvent) return;
    setInstalling(true);
    try {
      await promptEvent.prompt();
      await promptEvent.userChoice;
    } finally {
      setInstalling(false);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-gray-950/50 px-4 pb-4 sm:items-center sm:pb-0 animate-fade-in">
        <section
          role="dialog"
          aria-label="ホーム画面に追加"
          className="w-full max-w-md rounded-3xl bg-white p-5 shadow-xl animate-slide-up"
        >
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-lg font-black text-gray-900">ホーム画面に追加</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="閉じる"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-gray-200"
            >
              <X size={20} strokeWidth={1.75} />
            </button>
          </div>

          <p className="mt-2 text-sm font-bold leading-6 text-gray-500">
            {lead || 'アプリのように、ホーム画面のアイコンからすぐ開けるようになります。'}
          </p>

          {promptEvent && !platform.inLine ? (
            <button
              type="button"
              onClick={installNow}
              disabled={installing}
              className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-3xl bg-gray-900 text-base font-black text-white transition hover:bg-gray-800 active:scale-[0.98] disabled:opacity-60"
            >
              <Plus size={18} />
              ホーム画面に追加する
            </button>
          ) : (
            <ol className="mt-3 divide-y divide-gray-100">
              {steps.map((step, index) => (
                <Step key={step.title} number={index + 1} {...step} />
              ))}
            </ol>
          )}

          <div className="mt-3 rounded-xl bg-gray-50 px-4 py-3 text-xs font-bold leading-5 text-gray-500">
            ホーム画面のアイコンから開いたら、はじめに一度だけログインしてください。
          </div>

          <button
            type="button"
            onClick={onClose}
            className="mt-4 flex h-12 w-full items-center justify-center rounded-xl border-2 border-gray-200 bg-white text-sm font-black text-gray-700 transition hover:border-gray-300"
          >
            閉じる
          </button>
        </section>
    </div>
  );
}
